import React, {useEffect, useState} from 'react';
import LoginInputLabel from '@components/input-label/LoginInputLabel';
import LoginTextField from '@components/text-field/LoginTextField';
import Button from '@material-ui/core/Button';
import classNames from 'classnames';
import 'styles/Login.styl';
import 'styles/PasswordNew.styl';
import {PasswordSource} from '@store/password/PasswordSource';
import {browserHistory} from 'react-router';
import {
  getStrengthClassName,
  isPasswordStrengthSufficient,
  strength,
} from '@components/password/utils';

/**
 * Number the counter of weak passwords needs to reach before highlighting the strength requirements text.
 */
const COUNTER_HIGHLIGHTING_THRESHOLD = 2;

interface NewPasswordFormProps {
  code: string;
}

interface CounterState {
  value: number;
  passwordWhenSet: string;
}

export const PasswordResetForm: React.FC<NewPasswordFormProps> = ({code}) => {
  const [passwordStrength, setPasswordStrength] = useState<number | undefined>(0);
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [counterState, setCounterStateState] = useState<CounterState>({
    value: 0,
    passwordWhenSet: password,
  });
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  // Estimate password strength.
  useEffect(() => {
    setPasswordStrength(strength(password));
  }, [password]);

  // Validation.
  const passwordsMatch = password === passwordConfirm;
  const passwordsAreNonempty = !!password && !!passwordConfirm;
  const passwordIsStrongEnough = isPasswordStrengthSufficient(passwordStrength);
  const valid = passwordsMatch && passwordsAreNonempty && passwordIsStrongEnough;

  // See how the user is doing. If the passwords match but aren't strong enough, increment a counter. If the counter
  // reaches the threshold, we highlight the strength requirements text.
  const {value: counterStateValue, passwordWhenSet: counterStatePassword} = counterState;
  useEffect(() => {
    // Only change the counters when the user has typed the same password in both boxes AND the password is different
    // from the last time we checked.
    if (passwordsMatch && passwordsAreNonempty && password !== counterStatePassword) {
      if (passwordIsStrongEnough) {
        // Password's good enough for us.
        setCounterStateState({value: 0, passwordWhenSet: password});
      } else {
        if (counterStateValue <= COUNTER_HIGHLIGHTING_THRESHOLD) {
          // It never goes back down unless the password is set successfully, so no sense in increasing it beyond the
          // threshold.
          setCounterStateState({value: counterStateValue + 1, passwordWhenSet: password});
        }
      }
    }
  }, [
    password,
    passwordsMatch,
    passwordsAreNonempty,
    passwordIsStrongEnough,
    // Avoid using the object here, since it'll be different every time the state is updated, even if the values are
    // the same.
    counterStateValue,
    counterStatePassword,
  ]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    // Don't submit if not valid.
    if (valid) {
      const onSuccess = () => {
        setSuccess(true);
        setError(false);
        setCounterStateState({value: 0, passwordWhenSet: ''});
        // redirect to 'login', with enough time to see success message
        setTimeout(() => {
          browserHistory.replace('/login');
        }, 2000);
      };

      const onError = () => {
        setSuccess(false);
        setError(true);
      };

      PasswordSource.resetPasswordWithCode(code, password, passwordConfirm)
        .then(onSuccess)
        .catch(onError);
    }
  };

  const strengthMeterClassName = classNames('meter', getStrengthClassName(passwordStrength));
  const hintClassName =
    counterState.value > COUNTER_HIGHLIGHTING_THRESHOLD ? 'highlighted' : 'normal';

  return (
    <form className="login-component" onSubmit={submit}>
      <div className="form-text">Enter your new password below</div>
      <LoginInputLabel htmlFor="new-password" className="text-field-label">
        Password
      </LoginInputLabel>
      <LoginTextField
        id="new-password"
        name="password"
        className="text-field password"
        fullWidth={true}
        onChange={e => setPassword(e.target.value)}
        type="password"
        value={password}
      />
      <div className="password-strength">
        <div className="label">Password strength</div>
        <div className={strengthMeterClassName}></div>
      </div>
      <LoginInputLabel htmlFor="repeat-password" className="text-field-label">
        Repeat your new password
      </LoginInputLabel>
      <LoginTextField
        id="repeat-password"
        name="password2"
        className="text-field password"
        fullWidth={true}
        onChange={e => setPasswordConfirm(e.target.value)}
        type="password"
        value={passwordConfirm}
      />
      {error && <div className="error-message">There was an error updating your password.</div>}
      <Button
        className="submit-button"
        type="submit"
        onClick={submit}
        disabled={!valid}
        size="large"
        color="primary"
        variant="contained">
        Submit
      </Button>
      {success && <div className="form-text message">Your password was successfully updated.</div>}
      <div
        className="support-button"
        onClick={() => window.open('https://support.mediafly.com/hc/en-us', '_blank')}>
        Get Support
      </div>
      <div className="hint">
        <div className={hintClassName}>
          <div>
            Your Coach password needs to meet the high strength requirements created by NIST.
          </div>
          <div>
            To create a strong password, choose a short phrase that is memorable to you, and modify
            it by changing letter case or swapping characters for numbers or symbols until the
            password strength bar goes green.
          </div>
        </div>
      </div>
    </form>
  );
};
